/**
 * Keeps an expired CSRF token from ever reaching the customer as Livewire's "Page Expired" artefact.
 *
 * Livewire 3.6.4 answers a 419 by calling `confirm("This page has expired...")` and then — whether or
 * not the confirm was accepted — `showFailureModal()`, which prepends a fixed full-viewport scrim
 * holding an iframe of the raw 419 page. `window.location.reload()` queues a navigation but does not
 * halt the script, so even the "OK" path paints the error page before the reload lands: that is the
 * flash. A `fail` callback calling `preventDefault()` short-circuits above both branches, so one hook
 * is the entire kill switch and no vendor patch is needed (which matters — vendor/ is gitignored and
 * refetched by composer on every deploy). See vendor/livewire/livewire/dist/livewire.js:4311-4350.
 *
 * Suppressing the artefact is only half the job; the page would still hold a dead token. It can be
 * repaired in place because a 419 here is almost never a logout: `AuthenticateSession` runs at
 * web-group position 4 and `VerifyCsrfToken` at 6, and remember-me is on for effectively every account
 * (the login checkbox ships `checked`; Google sign-in passes `true`), so the recaller has already
 * signed the customer back in by the time CSRF rejects the stale token — and `SessionGuard::migrate()`
 * does not rotate `_token`, so the token the page is missing is simply the newly-minted one. Writing
 * the session's current token into the `csrf-token` meta tag is therefore enough: Livewire's
 * `getCsrfToken()` re-reads that tag on every request, so the next commit succeeds with no reload
 * (dist:422-432, 572, 4268).
 *
 * The "almost" is why the endpoint also reports whether the caller is still authenticated. Cleared
 * cookies, a private window, a password change and "log out other browser sessions" all produce a
 * stale token on a page whose viewer really has been signed out; repairing the token there would leave
 * them clicking a button that quietly does nothing.
 */

const TOKEN_ENDPOINT = '/session/token';

/**
 * A gesture this recent means a person is sitting there waiting on the request that just failed, so
 * the failure has to be spoken. Older than this and it was almost certainly one of the background
 * `wire:poll` bindings, which nothing is waiting on and whose next tick retries for free. Livewire
 * gives no way to tell the two apart at the request layer — `wire:poll="method"` evaluates
 * `$wire.method` exactly as a click does (dist:10051-10064) — so recency of intent is the signal.
 */
const GESTURE_WINDOW_MS = 10000;

/** Backs a failing page off. Only a FAILED attempt starts it, so a working page is never throttled. */
const REPAIR_COOLDOWN_MS = 30000;

/**
 * Hard stop after this many failures in a row, cleared by any attempt or commit that succeeds. The
 * cooldown alone would let a permanently-broken page (a browser refusing our cookie, say) keep probing
 * for as long as it stays open. Counting failures rather than attempts matters because the pre-emptive
 * refresh below shares this budget: charging it for successful refreshes would let an idle tab exhaust
 * the allowance it needs for a real 419 later.
 */
const MAX_CONSECUTIVE_FAILURES = 5;

/**
 * How long after the `load` event to raise the notice that a plain form's post was rejected.
 *
 * Anchored to `load`, not to Alpine starting, because the wait is for VoiceOver to finish announcing
 * the new page: a live-region write made while it is still talking is dropped silently and never
 * spoken. `load` is the only signal here that scales with how much page there is to announce, which
 * is why a fixed delay from Alpine start worked on a stripped-down test page (3 of 3) and failed on
 * a real app screen.
 */
const NOTICE_AFTER_LOAD_MS = 1500;

/** Collapses the several toasts that would otherwise fire when two pools fail in the same tick. */
const ANNOUNCE_DEBOUNCE_MS = 2000;

/**
 * How long the tab must have been hidden before returning to it is worth a pre-emptive token refresh.
 * The dominant trigger for a 419 here is not an idle user but a tab whose timers stopped — a slept
 * laptop, a frozen or discarded background tab: while timers run, every `wire:poll` re-stamps the
 * session and it never expires. The client cannot know SESSION_LIFETIME, so this is a heuristic, and
 * it is only an optimisation — the 419 hook below is never made conditional on it.
 */
const STALE_HIDDEN_MS = 5 * 60 * 1000;

let lastGestureAt = 0;
let lastAnnouncedAt = 0;
let hiddenSince = 0;
let cooldownUntil = 0;
let consecutiveFailures = 0;
let repairInFlight = null;
/**
 * Latched, because a dead session does not come back on its own. Without it every subsequent poll
 * would re-probe the endpoint and raise another notice, stacking one toast per poll interval at
 * somebody who is not even there. Cleared only by the page reloading.
 */
let signedOut = false;
let signedOutAnnounced = false;

/**
 * `click` is not redundant beside `pointerdown`. When VoiceOver (VO+Space) or Voice Control activates
 * a button in Safari, WebKit's SimulatedClick.cpp dispatches mousedown/mouseup/click and **no**
 * pointerdown, and VoiceOver swallows its own keystrokes so no keydown arrives either — so without
 * `click` an assistive-technology user's failed action was the one case that stayed silent. Chromium
 * does send pointerdown for the same activation, which is why this only showed up in WebKit.
 *
 * `change` covers a <select> bound with wire:model.live, which VoiceOver changes without any click.
 *
 * `input` is deliberately NOT here: sync-manual-data-v2.js dispatches it programmatically in a loop
 * (three call sites), which would fake a gesture with nobody present and turn a silent poll recovery
 * into a toast. Typing already arrives as keydown.
 *
 * Nothing in this list can be raised by a wire:poll, which is what keeps background recovery silent.
 */
['pointerdown', 'keydown', 'click', 'change'].forEach((event) => {
    document.addEventListener(event, () => {
        lastGestureAt = Date.now();
    }, { capture: true, passive: true });
});

function metaContent(name) {
    return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') ?? null;
}

/**
 * Resolves 'repaired' when the page holds a usable token again, 'signed-out' when the probe says the
 * viewer is no longer authenticated on a page that was rendered authenticated, or 'failed'.
 *
 * Single-flighted: a pool of components failing together must produce one request, not one each.
 */
function repairToken() {
    if (signedOut) {
        return Promise.resolve('signed-out');
    }

    if (repairInFlight) {
        return repairInFlight;
    }

    if (Date.now() < cooldownUntil || consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        return Promise.resolve('failed');
    }

    repairInFlight = (async () => {
        try {
            const response = await fetch(TOKEN_ENDPOINT, {
                headers: {
                    Accept: 'application/json',
                    // Not decoration: StartSession::storeCurrentUrl() records every non-ajax GET as
                    // the session's "previous" URL, and UrlGenerator::previous() falls back to it when
                    // a request carries no Referer. Without this header, a form POST that then 419s
                    // would be redirected back to this JSON endpoint instead of to the form.
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'same-origin',
                cache: 'no-store',
            });

            if (!response.ok) {
                return 'failed';
            }

            const { token, authenticated } = await response.json();

            // Only a page that WAS authenticated can have been signed out. A public dashboard or
            // connection-request link is rendered with '0' and must never be sent to a login screen.
            if (metaContent('session-authenticated') === '1' && authenticated === false) {
                signedOut = true;

                return 'signed-out';
            }

            const meta = document.querySelector('meta[name="csrf-token"]');

            if (!token || !meta) {
                return 'failed';
            }

            meta.setAttribute('content', token);

            // Livewire prefers the meta tag but falls back to this, and any @csrf input already
            // rendered on the page is stale too — a plain form post would 419 all over again.
            if (window.livewireScriptConfig) {
                window.livewireScriptConfig.csrf = token;
            }

            document.querySelectorAll('input[name="_token"]').forEach((input) => {
                input.value = token;
            });

            return 'repaired';
        } catch (error) {
            // Degrading is the point — this runs inside Livewire's failure path, and throwing here
            // would replace a handled 419 with an unhandled rejection — but it must not be silent, or
            // a page that stops recovering gives no clue why.
            console.warn('Session token refresh failed', error);

            return 'failed';
        } finally {
            repairInFlight = null;
        }
    })().then((outcome) => {
        // Only a failure earns a cooldown. A page that recovers cleanly must be free to recover again
        // on the next 419, and the pre-emptive refresh must not spend the recovery allowance.
        if (outcome === 'failed') {
            consecutiveFailures += 1;
            cooldownUntil = Date.now() + REPAIR_COOLDOWN_MS;
        } else {
            consecutiveFailures = 0;
            cooldownUntil = 0;
        }

        return outcome;
    });

    return repairInFlight;
}

/**
 * The toast host is on both layouts, but the store is registered from app.js's `alpine:init`, so on a
 * page that failed before Alpine booted there is nothing to speak through — stay quiet rather than
 * throw inside Livewire's failure path.
 */
function announce(outcome, { persistent = false } = {}) {
    if (Date.now() - lastAnnouncedAt < ANNOUNCE_DEBOUNCE_MS) {
        return;
    }

    const store = window.Alpine?.store('toastStore');

    if (!store) {
        return;
    }

    lastAnnouncedAt = Date.now();

    store.addToast(outcome === 'repaired'
        ? {
            message: 'Your session was refreshed',
            description: 'That last action did not go through. Please try it again.',
            icon: 'info',
        }
        : {
            message: 'Your session has expired',
            description: 'Reload the page to carry on.',
            icon: 'warning',
            persistent,
        });
}

/**
 * Pre-emptive half: refresh the token when a tab comes back after long enough that its session may
 * have lapsed while its timers were stopped, and after a back/forward-cache restore, whose page was
 * serialised with whatever token it had at the time.
 */
document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
        hiddenSince = Date.now();
        return;
    }

    if (hiddenSince && Date.now() - hiddenSince > STALE_HIDDEN_MS) {
        repairToken();
    }

    hiddenSince = 0;
});

window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
        repairToken();
    }
});

/**
 * Raises the notice left by a plain form post that a stale token bounced back
 * (<x-session-expired-notice />, flashed by App\Exceptions\Handler). Persistent, because a
 * screen-reader user reaches it well after a sighted one, and because the whole point is that it
 * cannot be missed.
 */
function raiseNoticeLeftByAStaleForm() {
    const marker = document.querySelector('[data-session-expired-notice]');

    if (!marker) {
        return;
    }

    const store = window.Alpine?.store('toastStore');

    if (!store) {
        return;
    }

    store.addToast({
        message: marker.dataset.message,
        description: marker.dataset.description,
        icon: 'info',
        persistent: true,
    });
}

if (document.readyState === 'complete') {
    setTimeout(raiseNoticeLeftByAStaleForm, NOTICE_AFTER_LOAD_MS);
} else {
    window.addEventListener('load', () => {
        setTimeout(raiseNoticeLeftByAStaleForm, NOTICE_AFTER_LOAD_MS);
    }, { once: true });
}

document.addEventListener('livewire:init', () => {
    window.Livewire.hook('request', ({ fail, succeed }) => {
        succeed(() => {
            // The page is demonstrably healthy, so a run of earlier failures stops counting against it.
            consecutiveFailures = 0;
            cooldownUntil = 0;
        });

        fail(({ status, preventDefault }) => {
            if (status !== 419) {
                return;
            }

            // Kills both the confirm() and the full-screen 419 overlay. Livewire has already run
            // handleFailure() by now, so loading and dirty states are cleared and the UI is usable.
            preventDefault();

            const someoneWasWaiting = Date.now() - lastGestureAt < GESTURE_WINDOW_MS;

            repairToken().then((outcome) => {
                if (outcome === 'signed-out') {
                    // Reload rather than navigate to a login URL of our own: a GET of this page now
                    // passes through the app's `auth` middleware, which is what decides where a
                    // signed-out visitor goes and records the intended URL.
                    //
                    // Only when somebody is actually there, though. A reload discards whatever they
                    // had typed, and doing that off a background poll means a half-written form
                    // vanishes while they are reading something else, with no chance to copy it out.
                    // Waiting costs nothing: the page is already unable to save, and their next
                    // action 419s again — this time with a fresh gesture — and reloads then.
                    if (someoneWasWaiting) {
                        window.location.reload();
                    } else if (!signedOutAnnounced) {
                        signedOutAnnounced = true;
                        // Persistent, because this one fires precisely when nobody is looking: the
                        // no-gesture branch needs ten quiet seconds to be reached. A 5s dismissal
                        // plus the latch meant the warning came and went unseen, and the customer
                        // came back, clicked, and lost their typed text anyway — one click later
                        // than before, which is not the point of not reloading.
                        announce('failed', { persistent: true });
                    }

                    return;
                }

                // A background poll nobody was waiting on: the repaired token makes the next tick
                // succeed, so there is nothing worth saying. Only speak when a person was waiting —
                // a failed action must never look like it worked. Staying quiet when the repair
                // failed and nobody was there is deliberate: their first interaction will 419 and
                // surface it then, whereas announcing every quiet tick would stack toasts up.
                if (someoneWasWaiting) {
                    announce(outcome);
                }
            });
        });
    });
});
